import { use, createContext, useEffect, useRef, useState, type JSX, type RefObject } from 'react'
import { split } from '@chakra-ui/utils'
import { layoutPropNames } from '@chakra-ui/styled-system'
import { mergeRefs } from '~/common/reactUtils'
import { TOP_NAV_HEIGHT_PX } from '~/common/scrollUtils'
import { JumpToAnchor, useJumpToCategory } from '~/design_system/modules/lists/JumpTo'
import { RegionErrorBoundary } from '~/design_system/components/empty_states/EmptyStates'
import { Box, BoxProps, Divider, StackProps, Flex, Stack, Text, forwardRef } from '../../chakra'
import useUuid from '../../../hooks/useUuid'

type InsetDividerOptions = {
  orientation?: 'horizontal' | 'vertical'
  /** Chakra space token trimmed off both ends of the line. */
  inset?: number
  /** Match the owner's hover background transition so the line fades out with it. */
  fadeDuration?: string
}

/**
 * Canvas-only 1px divider styles for a `::before` / `::after`, used inside `$thm()`. Lets a divider
 * stop short of the edges (e.g. line up with row text) where a border can't. Pinned to the top
 * (horizontal) or left (vertical) edge; spread and override `top` / `left` to move it.
 *
 * @example
 * ```tsx
 * sx={$thm({
 *   position: { $canvas: 'relative' },
 *   '&:not(:first-of-type)::before': insetDivider(),
 *   '&:hover::before': { opacity: 0 }
 * })}
 * ```
 */
export function insetDivider({
  orientation = 'horizontal',
  inset = 4,
  fadeDuration = 'normal'
}: InsetDividerOptions = {}) {
  const line = orientation === 'horizontal' ? { top: 0, insetX: inset, h: '1px' } : { left: 0, insetY: inset, w: '1px' }
  return {
    content: { $canvas: '""' },
    position: 'absolute',
    ...line,
    bg: 'var(--cnvs-sys-color-border-divider)',
    pointerEvents: 'none',
    transitionProperty: 'opacity',
    transitionDuration: fadeDuration
  }
}

export const ScrollingRegion = forwardRef<BoxProps, 'div'>((props, ref) => {
  return (
    <Box
      data-scrolling-region
      ref={ref}
      overflowY="auto"
      tabIndex={0}
      role="region"
      _focusVisible={{ boxShadow: 'var(--chakra-shadows-outline)' }}
      {...props}
    />
  )
})

const useScrollEdgeFades = (scrollRef: RefObject<HTMLElement | null>) => {
  const SCROLL_EDGE_FADE_DISTANCE_PX = 50
  const [topOpacity, setTopOpacity] = useState(0)
  const [bottomOpacity, setBottomOpacity] = useState(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el
      const maxScrollTop = scrollHeight - clientHeight
      if (maxScrollTop <= 0) {
        setTopOpacity(0)
        setBottomOpacity(0)
        return
      }

      setTopOpacity(Math.min(1, Math.max(0, scrollTop / SCROLL_EDGE_FADE_DISTANCE_PX)))
      setBottomOpacity(Math.min(1, Math.max(0, (maxScrollTop - scrollTop) / SCROLL_EDGE_FADE_DISTANCE_PX)))
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(el)
    const mutationObserver = new MutationObserver(update)
    mutationObserver.observe(el, { childList: true, subtree: true })

    return () => {
      el.removeEventListener('scroll', update)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [scrollRef])

  return { topOpacity, bottomOpacity }
}

const scrollFadeOverlaySx = (location: 'top' | 'bottom', opacity: number) => {
  const SCROLL_EDGE_FADE_SIZE = '16px'
  const SCROLL_EDGE_SHADOW = 'rgba(0, 0, 0, 0.15)'
  const yPos = location === 'top' ? '3%' : '97%'
  return {
    position: 'absolute' as const,
    left: 0,
    right: 0,
    height: SCROLL_EDGE_FADE_SIZE,
    pointerEvents: 'none' as const,
    zIndex: 1,
    opacity,
    background: `radial-gradient(ellipse farthest-side at center ${yPos}, ${SCROLL_EDGE_SHADOW} 0%, transparent 100%)`,
    [location]: 0
  }
}

/**
 * A scrollable box that shows soft edge shadows when there is more content
 * above or below the viewport. Shadows are absolute overlays on a layout wrapper
 * so they stay pinned in a sticky expander, and still show when children have
 * their own background colors.
 *
 * The public ref is the inner scrolling element (`ScrollingRegion`).
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-separators--docs
 */
export const ScrollShadowBox = forwardRef<BoxProps, 'div'>(({ sx, ...props }, ref) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { topOpacity, bottomOpacity } = useScrollEdgeFades(scrollRef)

  // Outer box owns layout in the parent (size, flex, margin). Inner owns
  // scrolling. Overflow stays inner so this wrapper never becomes the
  // scrollport. Inner uses flex fill, not height: 100%, so maxH-capped lists
  // like FilterSidebar still shrink and scroll instead of growing with content.
  const [layoutProps, rest] = split(props, layoutPropNames as any)
  const { overflow, overflowX, overflowY, ...outerLayout } = layoutProps as BoxProps

  return (
    <Box
      h="full"
      minH={0}
      borderRadius="inherit"
      {...outerLayout}
      position="relative"
      display="flex"
      flexDir="column"
      sx={sx}>
      <ScrollingRegion
        data-scroll-shadow-box
        ref={mergeRefs(ref, scrollRef)}
        flex="1"
        minH={0}
        w="full"
        borderRadius="inherit"
        zIndex={0}
        overflow={overflow}
        overflowX={overflowX}
        overflowY={overflowY ?? 'auto'}
        {...rest}
      />
      <Box aria-hidden sx={scrollFadeOverlaySx('top', topOpacity)} />
      <Box aria-hidden sx={scrollFadeOverlaySx('bottom', bottomOpacity)} />
    </Box>
  )
})

ScrollShadowBox.displayName = 'ScrollShadowBox'

const SectionContext = createContext({ headingId: '' })

/**
 * Section
 *
 * A container for a SectionHeader and SectionContent that provides the correct accessibility and spacing.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-separators--docs
 */
export const Section = forwardRef(({ children, ...props }: StackProps, ref) => {
  const headingId = 'section-header-' + useUuid()
  return (
    <SectionContext value={{ headingId }}>
      <Stack
        data-section-container
        role="region"
        as="section"
        spacing="lg"
        ref={ref}
        aria-labelledby={headingId}
        // When we scroll to this section from a "Jump to" link, we want to offset the scroll position
        // by the height of the top nav so that the section header is not hidden behind it.
        scrollMarginTop={TOP_NAV_HEIGHT_PX}
        {...props}>
        <RegionErrorBoundary>{children}</RegionErrorBoundary>
      </Stack>
    </SectionContext>
  )
})

Section.displayName = 'Section'

export type SectionHeaderProps = {
  size?: 'sm' | 'md'
} & BoxProps

const SectionHeaderRoot = forwardRef(({ size = 'md', children, ...props }: SectionHeaderProps, ref) => {
  const headingId = use(SectionContext)?.headingId
  return (
    <Flex
      as="h2"
      id={headingId}
      data-section-header
      px={4}
      py={size === 'sm' ? 2 : 4}
      textStyle="sectionHeading"
      bg="gray.100"
      alignItems="center"
      justifyContent="space-between"
      ref={ref}
      {...props}>
      {children}
    </Flex>
  )
})

/**
 * Section Header Subtitle
 *
 * A smaller text displayed above the main section title. Use as a compound component:
 *
 * ```tsx
 * <SectionHeader>
 *   <SectionHeader.Subtitle>Category</SectionHeader.Subtitle>
 *   Main Title
 * </SectionHeader>
 * ```
 */
const SectionHeaderSubtitle = ({ children, ...props }: BoxProps) => (
  <Text textStyle="subtextSmall" {...props}>
    {children}
  </Text>
)

SectionHeaderSubtitle.displayName = 'SectionHeader.Subtitle'

/**
 * Section Header
 *
 * Forms often work best when broken into sections, grouping form elements according to their purpose – if it were
 * an order page you might put items in one group, customization in another, and delivery in a third.
 *
 * This can also be used to break up data heavy read-only pages (Work Order Detail and Vendor Profile)
 *
 * Supports a compound `Subtitle` component for category text:
 * ```tsx
 * <SectionHeader>
 *   <SectionHeader.Subtitle>General</SectionHeader.Subtitle>
 *   Work Order Details
 * </SectionHeader>
 * ```
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-separators--docs
 */
export const SectionHeader = Object.assign(SectionHeaderRoot, {
  Subtitle: SectionHeaderSubtitle
})

SectionHeader.displayName = 'SectionHeader'

export type SectionSubHeaderProps = {
  /** Whether to show a divider above the sub-header */
  showDivider?: boolean
} & BoxProps

/**
 * Section Sub Header
 *
 * A smaller header (h2) for sub-sections within a main section.
 * Use this to break up content within a section without creating a new jump-to anchor.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-separators--docs
 */
export const SectionSubHeader = forwardRef(
  ({ showDivider = false, children, ...props }: SectionSubHeaderProps, ref) => {
    return (
      <>
        {showDivider && <Divider my={4} />}
        <Box as="h3" data-section-sub-header pb={5} textStyle="sectionSubheading" ref={ref} {...props}>
          {children}
        </Box>
      </>
    )
  }
)

SectionSubHeader.displayName = 'SectionSubHeader'

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export const JumpToSectionHeader = (
  props: SectionHeaderProps & { anchorId: string; sectionTitle: string | JSX.Element; category?: string }
) => {
  const { children, anchorId, sectionTitle, category: categoryProp, ...headerProps } = props
  const categoryFromContext = useJumpToCategory()
  const category = categoryProp ?? categoryFromContext

  return (
    <SectionHeader {...headerProps}>
      <Box>
        {category && <SectionHeader.Subtitle>{category}</SectionHeader.Subtitle>}
        <JumpToAnchor anchorId={anchorId} category={category}>
          {sectionTitle}
        </JumpToAnchor>
      </Box>
      {children}
    </SectionHeader>
  )
}

/**
 * Section Context
 *
 * Designed to fit under the SectionHeader and gives the appropriate amount of padding
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-separators--docs
 */
export const SectionContent = forwardRef(({ children, ...props }: BoxProps, ref) => (
  <Box px={4} ref={ref} {...props}>
    <RegionErrorBoundary>{children}</RegionErrorBoundary>
  </Box>
))

SectionContent.displayName = 'SectionContent'
